import LiveEvents from './LiveEvents';
import ServerError from './ServerError';
import * as calls from './calls';
import ApiState from './ApiState';
import * as customCalls from './custom';
import tradingDurations from './tradingDurations';
import { isPermanentAuthErrorCode } from './authErrors';

let _reqId = 1; const getUniqueId = () => _reqId++;

getUniqueId(); // skip 0 value

// A money request the caller already saw fail must never reach the socket afterwards: the caller
// may retry, and a late send would buy (or move money) a second time. Keyed on the message, so the
// high-level calls and the deprecated send()/sendRaw() path (copier bulk mirror) share one rule.
const NO_REPLAY_KEYS = ['buy', 'buy_contract_for_multiple_accounts', 'paymentagent_withdraw',
    'paymentagent_transfer', 'transfer_between_accounts', 'topup_virtual'];
const isNoReplay = json => NO_REPLAY_KEYS.some(k => json[k] !== undefined);
const defaultApiUrl = 'wss://api.derivws.com/trading/v1/options/ws/public';
const keepAliveIntervalMs = 30 * 1000; // Deriv API docs: send ping every 30 seconds
const nullFunc = () => { };
const MockWebSocket = nullFunc;
let WebSocket = typeof window !== 'undefined' ? window.WebSocket : MockWebSocket;

const shouldIgnoreError = (error) =>
    error.code === 'AlreadySubscribed';

export default class LiveApi {

    static tradingDurations = tradingDurations;

    static getStaticTradingDurations() {
        return tradingDurations;
    }
    constructor(initParams) {
        const params = initParams || {};
        const {
            apiUrl = defaultApiUrl,
            language = 'en',
            appId = 0,
            brand = '',
            sendSpy = () => { },
            websocket,
            connection,
            keepAlive,
            useRx = false,
            onReconnect,
            // Legacy mode support for v3 compatibility
            legacyMode = false,
            legacyToken = null,
            normalizeSymbols = true,
        } = params;

        // v4 public/OTP URLs must be used as-is; only append ?l=&app_id= for v3.
        // Default apiUrl is the v4 public endpoint, so rawApiUrl defaults to true there.
        const rawApiUrl = typeof params.rawApiUrl === 'boolean'
            ? params.rawApiUrl
            : apiUrl === defaultApiUrl;

        this.legacyMode = legacyMode;
        this.legacyToken = legacyToken;
        this.normalizeSymbols = normalizeSymbols;
        /** Set when authorize returns error; cleared on successful authorize. */
        this.lastAuthError = null;
        /** True when constructor was given a v3 API token that must authorize. */
        this._requiresAuthorize = !!legacyToken;

        this.apiUrl = apiUrl;
        this.language = language;
        this.appId = appId;
        this.brand = brand;
        this.sendSpy = sendSpy;
        // Public options WS must be used as-is (no legacy ?app_id= construction).
        this.rawApiUrl = rawApiUrl || (typeof apiUrl === 'string' && apiUrl.includes('/options/ws/public'));
        this.onReconnect = onReconnect;

        // useRx is deprecated; deriv-live-api-v2 uses native Promises
        if (useRx) {
            console.warn('useRx is deprecated in deriv-live-api-v2. Native Promises are used.');
        }
        this.useRx = false;

         this.isSwitchingUrl = false;
         this.keepAlive = !!keepAlive;
         this.keepAliveInterval = null;

         if (this.keepAlive) {
            this.startKeepAlive();
        }

        if (websocket) {
            WebSocket = websocket;
        }

        this.bufferedSends = [];
        this.bufferedExecutes = [];
        this.unresolvedPromises = {};

        this.tradingDurations = tradingDurations;

        this.events = new LiveEvents();
        this.apiState = new ApiState();

        // Backward compatibility for authStream (deprecated)
        this.authStream = {
            subscribe: (onNext) => {
                const handler = (data) => {
                    if (typeof onNext === 'function') onNext(data);
                };
                this.events.on('authorize', handler);
                return {
                    dispose: () => this.events.off('authorize', handler),
                };
            },
            take: (count) => ({
                subscribe: (onNext) => {
                    let taken = 0;
                    const handler = (data) => {
                        taken++;
                        if (typeof onNext === 'function') onNext(data);
                        if (taken >= count) {
                            this.events.off('authorize', handler);
                        }
                    };
                    this.events.on('authorize', handler);
                    return {
                        dispose: () => this.events.off('authorize', handler),
                    };
                },
            }),
        };

        this.bindCallsAndStateMutators();

        this.connect(connection);
    }

    bindCallsAndStateMutators() {
        Object.keys(calls).forEach(callName => {
            this[callName] = (...params) => {
                if (callName === 'getTradingDurations' && this.rawApiUrl) {
                    return Promise.resolve({ trading_durations: LiveApi.tradingDurations });
                }
                return this.sendAndUpdateState(callName, ...params);
            };
        });

        Object.keys(customCalls).forEach(callName => {
            this[callName] = (...params) => customCalls[callName](this, ...params); // seems to be a good place to do some simple cache
        });
    }

     getStaticTradingDurations() {
         return LiveApi.getStaticTradingDurations();
     }

     startKeepAlive = () => {
         if (!this.keepAlive || this.keepAliveInterval != null) {
             return;
         }
         this.keepAliveInterval = setInterval(() => {
             if (this.isReady()) {
                 this.ping().catch(() => { });
             }
         }, keepAliveIntervalMs);
     };

     stopKeepAlive = () => {
         if (this.keepAliveInterval != null) {
             clearInterval(this.keepAliveInterval);
             this.keepAliveInterval = null;
         }
     };

     /**
      * Validate WebSocket URL format
      * @param {string} url - URL to validate
      * @returns {boolean} True if valid
      * @private
      */
     _isValidWebSocketUrl(url) {
         try {
             const urlObj = new URL(url);
             return ['ws:', 'wss:'].includes(urlObj.protocol);
         } catch {
             return false;
         }
     }

      connect(connection) {
          if (this.keepAlive && this.keepAliveInterval == null) {
              this.startKeepAlive();
          }

          // Prevent double-connecting if the socket is already open or connecting
          if (!connection && this.socket && (this.socket.readyState === 0 || this.socket.readyState === 1)) {
              if (this.socket.readyState === 1) {
                  return Promise.resolve();
              }
              return new Promise(resolve => {
                  this.events.once('open', resolve);
              });
          }

         // rawApiUrl (v4): use apiUrl exactly as provided — no ?l= / app_id / brand.
         // v3: append language, appId, and optional brand query params.
         let wsUrl = this.apiUrl;
         if (!this.rawApiUrl) {
             wsUrl = `${this.apiUrl}?l=${this.language}&app_id=${this.appId}${this.brand ? `&brand=${this.brand}` : ''}`;
         }

          this._dropNoReplay(this.isSwitchingUrl ? 'AccountSwitchError' : 'DisconnectError', false);

          // Only reject unresolved promises if we're NOT in the middle of a switch
          // During a switch, we expect the socket to close and reconnect
          if (!this.isSwitchingUrl) {
              Object.keys(this.unresolvedPromises).forEach(reqId => {
                   const promise = this.unresolvedPromises[reqId];
                   if (promise) {
                       const disconnectedError = new Error(
                           'Websocket disconnected before response received.'
                       );
                       disconnectedError.name = 'DisconnectError';
                       promise.reject(disconnectedError);
                       delete this.unresolvedPromises[reqId];
                   }
               });
          } else {
              // During switch, clear unresolved promises without rejecting
              // They'll be re-sent after reconnection
              Object.keys(this.unresolvedPromises).forEach(reqId => {
                   delete this.unresolvedPromises[reqId];
              });
          }

          try {
              this.socket = connection || new WebSocket(wsUrl);
          } catch (err) {
              this.events.emit('error', err);
              console.error('[LiveApi] Connection error:', err);
              return Promise.reject(err);
          } finally {
             if (this.socket) {
                 this.socket.onopen = this.onOpen;
                 this.socket.onerror = (error) => {
                     // In Node.js (ws), unhandled socket errors crash the process.
                     // We must catch them here. The socket will naturally close afterwards,
                     // triggering our onclose -> reconnect logic.
                     this.events.emit('error', error);
                 };
                  this.socket.onclose = () => {
                      // On close, trigger reconnect with backoff
                      this.reconnect();
                  };
                 this.socket.onmessage = this.onMessage;
             }
          }

          return new Promise(resolve => {
              this.events.once('open', resolve);
          });
      }

    onOpen = () => {
        try {
            const resubscribeResult = this.resubscribe();
            if (resubscribeResult && typeof resubscribeResult.catch === 'function') {
                resubscribeResult.catch((err) => {
                    // Avoid unhandled rejections when auto-authorize fails (e.g. InvalidToken).
                    if (err) {
                        this.events.emit('error', err.error || err);
                    }
                });
            }
        } catch (err) {
            this.events.emit('error', err);
        }
        this.executeBufferedExecutes();
        this.events.emit('open');
    };

     disconnect = () => {
         this.token = '';
         if (this.socket) {
             this.socket.onclose = nullFunc;
             try {
                 this.socket.close();
             } catch (e) {
                 this.events.emit('error', e);
                 console.error('[LiveApi] Disconnect error:', e);
             }
         }
         this.stopKeepAlive();
     };

     reconnect = () => {
         if (this.socket && typeof this.socket.close === 'function') {
             this.socket.onclose = nullFunc;
             try {
                 this.socket.close();
             } catch (e) {
                 this.events.emit('error', e);
                 console.error('[LiveApi] Reconnect error:', e);
             }
         }
         // In rawApiUrl mode, fetch a fresh OTP URL before reconnecting
         if (this.rawApiUrl && typeof this.onReconnect === 'function') {
             Promise.resolve(this.onReconnect()).then(freshUrl => {
                 if (freshUrl) {
                     this.apiUrl = freshUrl;
                 }
                 this.connect();
             }).catch(() => {
                 this.connect();
             });
         } else {
             this.connect();
         }
     };

    /**
     * Switch to a different account or API endpoint.
     *
     * Supports two modes:
     * - **v4 (rawApiUrl)**: Closes the socket, connects to a new OTP URL, resolves on `onOpen`.
     * - **v3 (legacyToken)**: Re-authorizes on the existing open socket with a new token.
     *   If the socket is not open, it reconnects first.
     *
     * @param {Object} options
     * @param {string} [options.apiUrl] - WebSocket URL. Required for v4, optional for v3.
     * @param {boolean} [options.rawApiUrl=false] - Set true for v4 OTP connections.
     * @param {string} [options.legacyToken=null] - Legacy token for v3 account switching.
     * @param {number} [options.appId=null] - Optional appId override for v3.
     * @param {string} [options.language=null] - Optional language override for v3.
     * @param {Function} [options.onReconnect] - Callback for v4 OTP URL refresh on reconnect.
     * @param {boolean} [options.preserveState=true] - Whether to preserve API state across switch.
     * @param {number} [options.timeoutMs=10000] - Maximum time to wait for the switch.
     * @returns {Promise<void>} Resolves when the new connection is ready.
     */
    switchApiUrl = (params = {}) => {
        const {
            apiUrl,
            legacyToken,
            appId,
            language,
            onReconnect,
            preserveState = true,
            timeoutMs = 10000,
        } = params;

        // If apiUrl is passed, use v4 mode (rawApiUrl = true), otherwise use v3 mode.
        const rawApiUrl = typeof params.rawApiUrl === 'boolean' ? params.rawApiUrl : !!apiUrl;

        // Use provided apiUrl or fallback to default production URL
        const effectiveApiUrl = apiUrl || defaultApiUrl;

         // ─── Validation ───────────────────────────────────────────
         if (rawApiUrl) {
             // v4 mode: validate WebSocket URL structure
             if (!this._isValidWebSocketUrl(effectiveApiUrl)) {
                 return Promise.reject(new Error('switchApiUrl: apiUrl must be a valid WebSocket URL (ws:// or wss://)'));
             }
         } else {
             // v3 mode requires a token
             if (!legacyToken || typeof legacyToken !== 'string' || legacyToken.trim() === '') {
                 return Promise.reject(new Error('switchApiUrl: legacyToken is required for v3 account switching'));
             }
         }

         // Validate optional parameters
         if (appId !== undefined && (typeof appId !== 'number' || appId <= 0 || !Number.isInteger(appId))) {
             return Promise.reject(new Error('switchApiUrl: appId must be a positive integer'));
         }

         if (language !== undefined && (typeof language !== 'string' || language.length === 0)) {
             return Promise.reject(new Error('switchApiUrl: language must be a non-empty string'));
         }

         if (timeoutMs !== undefined && (typeof timeoutMs !== 'number' || timeoutMs <= 0)) {
             return Promise.reject(new Error('switchApiUrl: timeoutMs must be a positive number'));
         }

         // Clamp timeout to reasonable max (1 hour)
         if (timeoutMs > 3600000) {
             return Promise.reject(new Error('switchApiUrl: timeoutMs cannot exceed 1 hour (3600000ms)'));
         }

         if (onReconnect !== undefined && typeof onReconnect !== 'function') {
             return Promise.reject(new Error('switchApiUrl: onReconnect must be a function'));
         }

         if (preserveState !== undefined && typeof preserveState !== 'boolean') {
             return Promise.reject(new Error('switchApiUrl: preserveState must be a boolean'));
         }

        // Concurrency guard - must be checked BEFORE no-op check
        if (this.isSwitchingUrl) {
            return Promise.reject(new Error('switchApiUrl: switch already in progress'));
        }

        // No-op check: same token on v3 or same URL on v4
        if (!rawApiUrl && legacyToken === this.legacyToken) {
            return Promise.resolve();
        }
        if (rawApiUrl && apiUrl === this.apiUrl && rawApiUrl === this.rawApiUrl) {
            return Promise.resolve();
        }

        this.isSwitchingUrl = true;
        // Read by apps to end a buy retry chain that outlives a switch (it must never buy on the new account).
        this.switchCount = (this.switchCount || 0) + 1;

        // No timer exists before the v3/v4 flow starts, so a synchronous throw here would leave
        // isSwitchingUrl true for good and every later money request refused.
        try {
            this._dropNoReplay('AccountSwitchError', true);

            this.events.emit('switch_api_url_start', { apiUrl: effectiveApiUrl, rawApiUrl, legacyToken: !!legacyToken });

            if (!preserveState) {
                this.apiState.resetState();
            }

            // ─── v3 flow: re-authorize on existing socket ─────────────
            if (!rawApiUrl) {
                return this._switchV3({ legacyToken, appId, language, apiUrl: effectiveApiUrl, timeoutMs });
            }

            // ─── v4 flow: reconnect with new OTP URL ─────────────────
            return this._switchV4({ apiUrl: effectiveApiUrl, rawApiUrl, onReconnect, timeoutMs });
        } catch (e) {
            this.isSwitchingUrl = false;
            return Promise.reject(e);
        }
    };

    /**
     * Rejects pending no-replay requests (buys, transfers) with `errorName` and removes their
     * queued sends, so they can never execute after the caller saw them fail.
     * onlyBuffered: leave requests already on the wire; their reply can still arrive.
     */
    _dropNoReplay = (errorName, onlyBuffered) => {
        const buffered = new Set(this.bufferedSends.map(send => send.reqId).filter(Boolean));
        const dropped = new Set();
        Object.keys(this.unresolvedPromises).forEach(reqId => {
            const promise = this.unresolvedPromises[reqId];
            if (!promise || !promise.noReplay || (onlyBuffered && !buffered.has(reqId))) {
                return;
            }
            const error = new Error(errorName === 'AccountSwitchError'
                ? 'Account switch: request dropped, not re-sent (if it was already sent, its outcome is unknown).'
                : 'Websocket disconnected before response received.');
            error.name = errorName;
            promise.reject(error);
            delete this.unresolvedPromises[reqId];
            dropped.add(reqId);
        });
        if (dropped.size) {
            this.bufferedSends = this.bufferedSends.filter(send => !dropped.has(send.reqId));
        }
    };

    _refuseDuringSwitch = () => {
        const error = new Error('Account switch in progress: money request not sent.');
        error.name = 'AccountSwitchError';
        return Promise.reject(error);
    };

    /**
     * v3 account switch: re-authorize with a new legacy token.
     * If the socket is open, sends authorize directly.
     * If not, reconnects first, then authorizes.
     * @private
     */
    _switchV3 = ({ legacyToken, appId, language, apiUrl, timeoutMs }) => {
        // Save previous state for rollback
        const prevLegacyToken = this.legacyToken;
        const prevApiUrl = this.apiUrl;
        const prevAppId = this.appId;
        const prevLanguage = this.language;
        const prevUserAuth = this.userAuth;

        return new Promise((resolve, reject) => {
            let timer;

            const cleanup = () => {
                this.isSwitchingUrl = false;
                if (timer) clearTimeout(timer);
            };

            const rollback = () => {
                this.legacyToken = prevLegacyToken;
                this.apiUrl = prevApiUrl;
                this.appId = prevAppId;
                this.language = prevLanguage;
                this.userAuth = prevUserAuth;
            };

            // Timeout safeguard
            if (timeoutMs > 0) {
                timer = setTimeout(() => {
                    cleanup();
                    rollback();
                    const err = new Error(`switchApiUrl: timed out after ${timeoutMs}ms`);
                    err.name = 'SwitchApiUrlTimeoutError';
                    this.events.emit('switch_api_url_error', err);
                    reject(err);
                }, timeoutMs);
            }

            // Update state and token BEFORE attempting switch
            this.legacyToken = legacyToken;
            this.rawApiUrl = false;
            if (appId) this.appId = appId;
            if (language) this.language = language;
            if (apiUrl) this.apiUrl = apiUrl;

            // Also update ApiState token immediately to ensure resubscribe uses the new one
            if (this.apiState) {
                this.apiState.authorize(legacyToken);
            }

            // Clear account-exclusive subscriptions from state before resubscribing
            // These should NOT persist across account switches
            this.apiState.unsubscribeFromAllOpenContracts();
            this.apiState.unsubscribeFromAllProposals();

            // Clear userAuth ONLY after we've started the switch operation
            this.userAuth = null;

            let switchOperation;
            if (!(this.socket && this.socket.readyState === 1)) {
                // If socket is closed, we need to ensure onOpen correctly handles the switch.
                const originalOnOpen = this.onOpen;
                switchOperation = new Promise((res, rej) => {
                    this.onOpen = () => {
                        this.onOpen = originalOnOpen;
                        Promise.race([
                            this.resubscribe(),
                            new Promise(resolve => this.events.once('authorize', resolve))
                        ]).then(res).catch(rej);
                    };
                    this.reconnect();
                });
            } else {
                switchOperation = Promise.race([
                    this.resubscribe(),
                    new Promise(resolve => this.events.once('authorize', resolve))
                ]);
            }

            switchOperation.then(data => {
                cleanup();
                this.events.emit('switch_api_url_success', {
                    apiUrl: this.apiUrl,
                    rawApiUrl: false,
                    legacyToken: true,
                    account: data?.authorize,
                });
                resolve(data);
            }).catch(err => {
                rollback();
                cleanup();
                const error = new Error(`switchApiUrl: switch failed — ${err.message || 'Unknown error'}`);
                error.name = 'SwitchApiUrlError';
                this.events.emit('switch_api_url_error', error);
                reject(error);
            });
        });
    };

    /**
     * v4 OTP URL switch: close socket, connect to new URL, resolve on open.
     * @private
     */
    _switchV4 = ({ apiUrl, rawApiUrl, onReconnect, timeoutMs }) => {
        // Save previous state for rollback
        const prevApiUrl = this.apiUrl;
        const prevRawApiUrl = this.rawApiUrl;
        const prevOnReconnect = this.onReconnect;

        // Update state — this is a v4 flow, clear v3 auth
        this.apiUrl = apiUrl;
        this.rawApiUrl = rawApiUrl;
        if (typeof onReconnect !== 'undefined') {
            this.onReconnect = onReconnect;
        }
        this.userAuth = null;
        this.legacyToken = null;
        if (this.apiState && this.apiState.state) {
            this.apiState.state.token = null;
        }

        return new Promise((resolve, reject) => {
            let timer;

            const cleanup = () => {
                this.isSwitchingUrl = false;
                if (timer) clearTimeout(timer);
            };

            // Timeout safeguard
            if (timeoutMs > 0) {
                timer = setTimeout(() => {
                    cleanup();
                    const err = new Error(`switchApiUrl: timed out after ${timeoutMs}ms`);
                    err.name = 'SwitchApiUrlTimeoutError';
                    this.events.emit('switch_api_url_error', err);
                    reject(err);
                }, timeoutMs);
            }

            // Hook into onOpen
            const originalOnOpen = this.onOpen;
            this.onOpen = () => {
                this.onOpen = originalOnOpen;
                originalOnOpen();

                setTimeout(() => {
                    cleanup();
                    this.events.emit('switch_api_url_success', { apiUrl, rawApiUrl, legacyToken: false });
                    resolve();
                }, 0);
            };

            // Close and reconnect
            this.socket.onclose = nullFunc;
            try { this.socket.close(); } catch (e) { /* ignore */ }
            delete this.socket;

            this.connect();

            if (!this.socket) {
                cleanup();
                const err = new Error('switchApiUrl: failed to create socket');
                err.name = 'SwitchApiUrlError';
                this.events.emit('switch_api_url_error', err);
                return reject(err);
            }

            const originalOnError = this.socket.onerror;
            this.socket.onerror = (err) => {
                this.socket.onerror = originalOnError;

                if (typeof this.onReconnect === 'function') {
                    Promise.resolve(this.onReconnect()).then(freshUrl => {
                        if (freshUrl) this.apiUrl = freshUrl;
                        delete this.socket;
                        this.connect();
                        if (this.socket) {
                            const retryOnError = this.socket.onerror;
                            this.socket.onerror = (retryErr) => {
                                this.socket.onerror = retryOnError;
                                this.apiUrl = prevApiUrl;
                                this.rawApiUrl = prevRawApiUrl;
                                this.onReconnect = prevOnReconnect;
                                cleanup();
                                const error = new Error(`switchApiUrl: failed to connect — ${retryErr.message || 'Unknown socket error'}`);
                                error.name = 'SwitchApiUrlError';
                                this.events.emit('switch_api_url_error', error);
                                reject(error);
                            };
                        } else {
                            this.apiUrl = prevApiUrl;
                            this.rawApiUrl = prevRawApiUrl;
                            this.onReconnect = prevOnReconnect;
                            cleanup();
                            const error = new Error('switchApiUrl: failed to create fallback socket');
                            error.name = 'SwitchApiUrlError';
                            this.events.emit('switch_api_url_error', error);
                            reject(error);
                        }
                    }).catch(reconnectErr => {
                        this.apiUrl = prevApiUrl;
                        this.rawApiUrl = prevRawApiUrl;
                        this.onReconnect = prevOnReconnect;
                        cleanup();
                        const error = new Error(`switchApiUrl: failed to connect — ${reconnectErr.message}`);
                        error.name = 'SwitchApiUrlError';
                        this.events.emit('switch_api_url_error', error);
                        reject(error);
                    });
                } else {
                    this.apiUrl = prevApiUrl;
                    this.rawApiUrl = prevRawApiUrl;
                    this.onReconnect = prevOnReconnect;
                    cleanup();
                    const error = new Error(`switchApiUrl: failed to connect — ${err.message || 'Unknown socket error'}`);
                    error.name = 'SwitchApiUrlError';
                    this.events.emit('switch_api_url_error', error);
                    reject(error);
                }
            };
        });
    };


    resubscribe = () => {
        try {
            const { token } = this.apiState.getState();

            const { authorized, unauthorized } = this.resubscriptions;

            this.events.once('authorize', () => {
                Object.keys(authorized).forEach(msgType => authorized[msgType]());
                this.sendBufferedSends();
            });
            Object.keys(unauthorized).forEach(msgType => unauthorized[msgType]());

            if (this.rawApiUrl) {
                this.sendBufferedSends();
                setTimeout(() => {
                    this.events.emit('authorize', { authorize: { is_v4: true } });
                }, 0);
                return Promise.resolve({ authorize: { is_v4: true } });
            } else if (token || this.legacyToken) {
                const tokenToUse = this.legacyToken || token;
                const authPromise = this.authorize(tokenToUse);
                this.sendBufferedSends();
                return authPromise;
            } else {
                this.sendBufferedSends();
                return Promise.resolve();
            }
        } catch (e) {
            // ignore
        }
    };

    getInState = name => this.apiState.getState()[name];    /**
     * Normalize symbol fields for v3/v4 compatibility
     * Adds .symbol alias to .underlying_symbol and vice versa
     * For active_symbols, also normalizes display_name and underlying
     */
    normalizeSymbolFields = (data) => {
        if (!data) return data;

        // Handle active_symbols array
        if (data.active_symbols && Array.isArray(data.active_symbols)) {
            data.active_symbols.forEach(obj => {
                if (!obj || typeof obj !== 'object') return;

                // Handle symbol <-> underlying_symbol <-> underlying
                const sym = obj.symbol || obj.underlying_symbol || obj.underlying;
                if (sym) {
                    if (!obj.symbol) obj.symbol = sym;
                    if (!obj.underlying_symbol) obj.underlying_symbol = sym;
                    if (!obj.underlying) obj.underlying = sym;
                }

                // Handle display_name <-> underlying_symbol_name
                const name = obj.display_name || obj.underlying_symbol_name;
                if (name) {
                    if (!obj.display_name) obj.display_name = name;
                    if (!obj.underlying_symbol_name) obj.underlying_symbol_name = name;
                }

                // Handle symbol_type <-> underlying_symbol_type
                const type = obj.symbol_type || obj.underlying_symbol_type;
                if (type) {
                    if (!obj.symbol_type) obj.symbol_type = type;
                    if (!obj.underlying_symbol_type) obj.underlying_symbol_type = type;
                }
            });
        }

        // Handle tick events
        if (data.tick && typeof data.tick === 'object') {
            if (data.tick.underlying_symbol && !data.tick.symbol) {
                data.tick.symbol = data.tick.underlying_symbol;
            }
            if (data.tick.symbol && !data.tick.underlying_symbol) {
                data.tick.underlying_symbol = data.tick.symbol;
            }
        }

        // Handle ohlc events
        if (data.ohlc && typeof data.ohlc === 'object') {
            if (data.ohlc.underlying_symbol && !data.ohlc.symbol) {
                data.ohlc.symbol = data.ohlc.underlying_symbol;
            }
            if (data.ohlc.symbol && !data.ohlc.underlying_symbol) {
                data.ohlc.underlying_symbol = data.ohlc.symbol;
            }
        }

        // Handle proposal_open_contract
        if (data.proposal_open_contract && typeof data.proposal_open_contract === 'object') {
            const contract = data.proposal_open_contract;
            // Normalize symbol <-> underlying_symbol <-> underlying
            const sym = contract.symbol || contract.underlying_symbol || contract.underlying;
            if (sym) {
                if (!contract.symbol) contract.symbol = sym;
                if (!contract.underlying_symbol) contract.underlying_symbol = sym;
                if (!contract.underlying) contract.underlying = sym;
            }

            // Normalize id <-> contract_id
            const cId = contract.id || contract.contract_id;
            if (cId) {
                if (!contract.id) contract.id = cId;
                if (!contract.contract_id) contract.contract_id = cId;
            }

            // Normalize v3 entry_tick/exit_tick <-> v4 entry_spot/exit_spot
            if (typeof contract.entry_spot !== 'undefined' && typeof contract.entry_tick === 'undefined') {
                contract.entry_tick = contract.entry_spot;
            } else if (typeof contract.entry_tick !== 'undefined' && typeof contract.entry_spot === 'undefined') {
                contract.entry_spot = contract.entry_tick;
            }

            if (typeof contract.exit_spot !== 'undefined' && typeof contract.exit_tick === 'undefined') {
                contract.exit_tick = contract.exit_spot;
            } else if (typeof contract.exit_tick !== 'undefined' && typeof contract.exit_spot === 'undefined') {
                contract.exit_spot = contract.exit_tick;
            }

            // Ensure entry_tick and exit_tick are numbers
            if (typeof contract.entry_tick === 'string') contract.entry_tick = Number(contract.entry_tick);
            if (typeof contract.entry_spot === 'string') contract.entry_spot = Number(contract.entry_spot);
            if (typeof contract.exit_tick === 'string') contract.exit_tick = Number(contract.exit_tick);
            if (typeof contract.exit_spot === 'string') contract.exit_spot = Number(contract.exit_spot);

            // Also map time and display values if they changed
            if (typeof contract.entry_spot_time !== 'undefined' && typeof contract.entry_tick_time === 'undefined') {
                contract.entry_tick_time = contract.entry_spot_time;
            } else if (typeof contract.entry_tick_time !== 'undefined' && typeof contract.entry_spot_time === 'undefined') {
                contract.entry_spot_time = contract.entry_tick_time;
            }

            if (typeof contract.entry_spot_display_value !== 'undefined' && typeof contract.entry_tick_display_value === 'undefined') {
                contract.entry_tick_display_value = contract.entry_spot_display_value;
            } else if (typeof contract.entry_tick_display_value !== 'undefined' && typeof contract.entry_spot_display_value === 'undefined') {
                contract.entry_spot_display_value = contract.entry_tick_display_value;
            }

            if (typeof contract.exit_spot_time !== 'undefined' && typeof contract.exit_tick_time === 'undefined') {
                contract.exit_tick_time = contract.exit_spot_time;
            } else if (typeof contract.exit_tick_time !== 'undefined' && typeof contract.exit_spot_time === 'undefined') {
                contract.exit_spot_time = contract.exit_tick_time;
            }

            if (typeof contract.exit_spot_display_value !== 'undefined' && typeof contract.exit_tick_display_value === 'undefined') {
                contract.exit_tick_display_value = contract.exit_spot_display_value;
            } else if (typeof contract.exit_tick_display_value !== 'undefined' && typeof contract.exit_spot_display_value === 'undefined') {
                contract.exit_spot_display_value = contract.exit_tick_display_value;
            }

            // Fallback display values to the numeric tick/spot if they don't exist
            if (typeof contract.entry_tick_display_value === 'undefined' && typeof contract.entry_tick !== 'undefined' && contract.entry_tick !== null) {
                contract.entry_tick_display_value = contract.entry_tick.toString();
                contract.entry_spot_display_value = contract.entry_tick.toString();
            }
            if (typeof contract.exit_tick_display_value === 'undefined' && typeof contract.exit_tick !== 'undefined' && contract.exit_tick !== null) {
                contract.exit_tick_display_value = contract.exit_tick.toString();
                contract.exit_spot_display_value = contract.exit_tick.toString();
            }
        }

        // Handle portfolio contracts
        if (data.portfolio && data.portfolio.contracts && Array.isArray(data.portfolio.contracts)) {
            data.portfolio.contracts.forEach(contract => {
                if (contract.underlying_symbol && !contract.symbol) {
                    contract.symbol = contract.underlying_symbol;
                }
                if (contract.symbol && !contract.underlying_symbol) {
                    contract.underlying_symbol = contract.symbol;
                }
            });
        }

        // Handle transaction events
        if (data.transaction && typeof data.transaction === 'object') {
            if (data.transaction.underlying_symbol && !data.transaction.symbol) {
                data.transaction.symbol = data.transaction.underlying_symbol;
            }
            if (data.transaction.symbol && !data.transaction.underlying_symbol) {
                data.transaction.underlying_symbol = data.transaction.symbol;
            }
        }

        return data;
    };
    resubscriptions = {
        authorized: {
            balance: () => {
                if (this.getInState('balance')) {
                    this.subscribeToBalance();
                }
            },
            transaction: () => {
                if (this.getInState('transactions')) {
                    this.subscribeToTransactions();
                }
            },
            proposal_open_contract: () => {
                if (this.getInState('allContract')) {
                    this.subscribeToAllOpenContracts();
                }
                this.getInState('contracts').forEach(id =>
                    this.subscribeToOpenContract(id)
                );
            },
        },
        unauthorized: {
            tick: () => {
                if (this.getInState('ticks').size !== 0) {
                    this.subscribeToTicks([...this.getInState('ticks')]);
                }

                this.getInState('ticksHistory').forEach((param, symbol) =>
                    this.getTickHistory(symbol, param)
                );
            },
            ohlc: () => {
                this.getInState('candlesHistory').forEach((param, symbol) =>
                    this.getTickHistory(symbol, param)
                );
            },
            history: () => {
                // Handle ticks_history responses with msg_type 'history'
                this.getInState('ticksHistory').forEach((param, symbol) =>
                    this.getTickHistory(symbol, param)
                );
            },
            candles: () => {
                // Handle candles responses with msg_type 'candles'
                this.getInState('candlesHistory').forEach((param, symbol) =>
                    this.getTickHistory(symbol, param)
                );
            },
            proposal: () => {
                this.getInState('proposals').forEach(proposal =>
                    this.subscribeToPriceForContractProposal(proposal)
                );
            },
        },
    };
    queuedResubscriptions = new Set();
    shouldResubscribeOnError = (json) => {
        const { error } = json;

        const errorsRequiringReconnect = [
            'CallError',
            'WrongResponse',
            'GetProposalFailure',
            'ProposalArrayFailure',
            'ContractValidationError',
            'ContractCreationFailure',
        ];

        if (!error || !errorsRequiringReconnect.includes(error.code)) {
            return false;
        }

        // Always drop the connection and reconnect. Retrying on the same socket
        // keeps a poisoned connection alive; reconnect() opens a fresh socket and
        // resubscribe() restores all subscriptions from apiState.
        if (!this.queuedResubscriptions.has('reconnect')) {
            this.queuedResubscriptions.add('reconnect');
            setTimeout(() => {
                this.queuedResubscriptions.delete('reconnect');
                this.reconnect();
            }, 500);
        }
        return true;
    };
    changeLanguage = (ln) => {
        if (ln === this.language) {
            return;
        }
        // In rawApiUrl mode, language is not part of the URL — no-op
        if (this.rawApiUrl) {
            this.language = ln;
            return;
        }
        this.socket.onclose = nullFunc;
        try {
            this.socket.close();
        } catch (e) {
            // ignore the error
        }
        this.language = ln;
        this.connect();
    };

    isAuthorized = () => !!(this.userAuth && this.userAuth.authorize);

    getLastAuthError = () => this.lastAuthError;

    expectsAuthorization = () =>
        !!(this._requiresAuthorize || this.legacyToken || this.getInState('token'));

    isReady = (callName) => {
        if (!this.socket || this.socket.readyState !== 1) return false;

        // authorize call can always be sent if socket is open
        if (callName === 'authorize') return true;

        // For v4 (rawApiUrl), we are pre-authenticated via the OTP URL.
        if (this.rawApiUrl) return true;

        // For v3, if we intended to authorize (have a token), we are only ready
        // after the authorize response is received and stored in userAuth.
        if (this.expectsAuthorization()) {
            if (this.lastAuthError) return false;
            return !!this.userAuth;
        }

        // Public v3 connection is ready as soon as the socket is open.
        return true;
    };

    sendBufferedSends = () => {
        while (this.bufferedSends.length > 0) {
            this.bufferedSends.shift()();
        }
    };

    executeBufferedExecutes = () => {
        while (this.bufferedExecutes.length > 0) {
            this.bufferedExecutes.shift()();
        }
    };

    resolvePromiseForResponse = (json) => {
        if (typeof json.req_id === 'undefined') {
            return Promise.resolve();
        }

        const reqId = json.req_id.toString();
        const promise = this.unresolvedPromises[reqId];

        if (!promise) {
            if (json.error) {
                this.shouldResubscribeOnError(json);
            }

            return Promise.resolve();
        }

        delete this.unresolvedPromises[reqId];
        if (!json.error) {
            return promise.resolve(json);
        }

        // Check if we should resubscribe/reconnect before rejecting
        // If resubscription is handled, resolve the promise instead of rejecting
        // The reconnection will retry the operation automatically
        if (json.error && this.shouldResubscribeOnError(json)) {
            // Resolve the promise since reconnection will retry
            // This prevents unhandled promise rejections
            return promise.resolve(json);
        }

        if (!shouldIgnoreError(json.error)) {
            return promise.reject(new ServerError(json));
        }

        return Promise.resolve();
    };

    onMessage = (message) => {
        let json = JSON.parse(message.data);

        // Normalize symbol fields for v3/v4 compatibility
        if (this.normalizeSymbols) {
            json = this.normalizeSymbolFields(json);
        }

        if (json.msg_type === 'authorize' && json.error) {
            this.events.emit('error', json);
            this.lastAuthError = json;
            this.userAuth = null;

            const authErrorCode = json.error?.code;
            const isPermanent = isPermanentAuthErrorCode(authErrorCode);

            // Reject the in-flight authorize() promise with ServerError (not DisconnectError).
            const promiseResult = this.resolvePromiseForResponse(json);

            // Clear invalid tokens to prevent infinite retry loops
            this.legacyToken = null;
            this._requiresAuthorize = false;
            if (this.apiState && this.apiState.state) {
                this.apiState.state.token = null;
            }

            if (!isPermanent) {
                this.reconnect();
            }

            return promiseResult || Promise.resolve();
        }

        if (!json.error) {
            this.events.emit(json.msg_type, json);
            if (json.msg_type === 'authorize') {
                this.userAuth = json;
                this.lastAuthError = null;
            }
        } else {
            this.events.emit('error', json);
        }

        return this.resolvePromiseForResponse(json);
    };

    generatePromiseOrObservable = (json) => {
        const reqId = json.req_id.toString();

        return new Promise((resolve, reject) => {
            this.unresolvedPromises[reqId] = { resolve, reject, noReplay: isNoReplay(json) };
        });
    };

    sendAndUpdateState = (callName, ...param) => {
        const reqId = getUniqueId();
        const actualPayload = calls[callName](...param);
        const json = {
            req_id: reqId,
            ...actualPayload,
        };
        if (this.isSwitchingUrl && isNoReplay(json)) {
            return this._refuseDuringSwitch();
        }

        // Create promise FIRST so it's registered before we send
        // This ensures synchronous mock responses can resolve the promise
        let result;
        if (typeof json.req_id !== 'undefined') {
            // Set the stream id into ApiState so that we can unsubscribe later
            // TODOhackish and need redo, this depends on object identity to works!!!
            const setState = r => {
                if (!this.apiState[callName]) return r;

                if (r.proposal_open_contract && r.proposal_open_contract.id) {
                    this.apiState[callName](...param, r.proposal_open_contract.id);
                } else if (r.proposal && r.proposal.id) {
                    this.apiState[callName](...param, r.proposal.id);
                }
                return r;
            };

            result = this.generatePromiseOrObservable(json).then(setState);
        }

        const socketSend = () => {
            this.sendSpy(JSON.stringify(json));
            try {
                this.socket.send(JSON.stringify(json));
            } catch (e) {
                this.bufferedSends.push(socketSend);
                this.reconnect();
                return;
            }
            if (this.apiState[callName]) {
                this.apiState[callName](...param);
            }
        };
        socketSend.reqId = json.req_id.toString();

        if (this.isReady(callName)) {
            socketSend();
        } else {
            this.bufferedSends.push(socketSend);
        }

        return result;
    };

    execute = (func) => {
        if (this.isReady()) {
            func();
        } else {
            this.bufferedExecutes.push(func);
        }
    };

    // TODO: should we deprecate this? preserve for backward compatibility
    send = (json) => {
        console.warn("This method is deprecated, use high-level methods"); // eslint-disable-line
        const reqId = getUniqueId();
        return this.sendRaw({
            req_id: reqId,
            ...json,
        });
    };

    // TODO: should we deprecate this? preserve for backward compatibility
    sendRaw = (json) => {
        console.warn("This method is deprecated, use high-level methods"); // eslint-disable-line
        if (this.isSwitchingUrl && isNoReplay(json)) {
            return typeof json.req_id !== 'undefined' ? this._refuseDuringSwitch() : undefined;
        }
        const socketSend = () => {
            this.sendSpy(JSON.stringify(json));
            try {
                this.socket.send(JSON.stringify(json));
            } catch (e) {
                this.bufferedSends.push(socketSend);
                this.reconnect();

            }
        };
        if (typeof json.req_id !== 'undefined') {
            socketSend.reqId = json.req_id.toString();
        }
        if (this.isReady()) {
            socketSend();
        } else {
            this.bufferedSends.push(socketSend);
        }

        if (typeof json.req_id !== 'undefined') {
            return this.generatePromiseOrObservable(json);
        }

        return undefined;
    };
}
